import apiClient from "./apiClient";

export interface ReconstructedEventDto {
  role: string;
  eventTypeName: string;
  suggestedTimeUtc?: string;
  currentTimeUtc?: string;
  deltaMinutes?: number;
  odometer?: number;
  latitude?: number;
  longitude?: number;
  confidence: number;
  method: string; // "GpsProximity" | "OnSiteAtBoundary" | "Interpolated"
  isGpsMatched: boolean;
  note?: string;
  dwellMinutes?: number;
  distanceToPointMiles?: number;
}

export interface IntermediateStopDto {
  startUtc: string;
  endUtc: string;
  latitude?: number;
  longitude?: number;
  odometer?: number;
  category: string; // "CrewWait" | "FuelStop" | "DriverBreak" | "Inspection" | "AssistMovement" | "Unknown"
  confidence: number;
  dwellMinutes: number;
  distanceToPointMiles?: number;
  nearestClientPointId?: number;
  poiName?: string;
  poiCategory?: string;
  note: string;
}

export interface AssistAssessmentDto {
  isAssistPattern: boolean;
  confidence: number;
  stopCount: number;
  collinearity: number;
  spanMiles: number;
  flaggedOnTrip: boolean;
  note: string;
  declaredAssist: boolean;
  estAssistHours: number;
  assistMiles: number;
}

export interface PrePickupOriginDto {
  dwellStartUtc: string;
  departedUtc: string;
  dwellMinutes: number;
  milesFromPickup: number;
}

export interface TripEndingDto {
  returnedToStart: boolean;
  arrivedBackUtc?: string;
  lastKnownUtc?: string;
  lastKnownLatitude?: number;
  lastKnownLongitude?: number;
  distanceFromStartMiles?: number;
  note: string;
}

export type ReconciliationAction = "remove" | "retime" | "add";

export interface ReconciliationSuggestionDto {
  action: ReconciliationAction;
  eventTypeName: string;
  /** Numeric dbo.Event.EventType — needed to insert an "add". */
  eventType: number;
  driverEventId?: number;
  driverTimeUtc?: string;
  suggestedTimeUtc?: string;
  latitude?: number;
  longitude?: number;
  reason: string;
  confidence: number;
}

export interface TripReconstructionResponse {
  tripId: number;
  beginTripUtc?: string;
  endTripUtc?: string;
  unitNumber?: number;
  pickupAddressFromNotes?: string;
  dropoffAddressFromNotes?: string;
  usedGps: boolean;
  note?: string;
  events: ReconstructedEventDto[];
  intermediateStops: IntermediateStopDto[];
  assist?: AssistAssessmentDto;
  vehicleMismatch?: boolean;
  vehicleMismatchNote?: string;
  /** True when GPS shows the van turned around short of the drop-off — it never reached the destination. */
  destinationNotReached?: boolean;
  destinationNotReachedNote?: string;
  prePickupOrigin?: PrePickupOriginDto;
  tripEnding?: TripEndingDto;
  reconciliation: ReconciliationSuggestionDto[];
}

export type AutoClosePreviewChange = "inserted" | "odometer" | "same";

export interface AutoClosePreviewEvent {
  /** The event's line number in the auto-approver's ordering — the "Line N" fail reasons cite. */
  line?: number;
  eventTypeName: string;
  timeLocal: string;
  odometer?: number;
  routeLocation?: number;
  /** Display name of the point-of-service (resolved from routeLocation). */
  routeLocationName?: string;
  /** Event Comments — the wait reason for Wait Start/End rows. */
  comments?: string;
  change: AutoClosePreviewChange;
}

export interface AutoClosePreviewOutcome {
  wouldAutoClose: boolean;
  failReason?: string;
}

/** One existing driver event moved to its GPS-observed time. */
export interface RetimeSelection {
  eventId: number;
  newTimeUtc: string;
  /** The event's ORIGINAL time (UTC). Lets the server shift the local time by a timezone-invariant delta. */
  origTimeUtc?: string;
}

/** One event GPS proves happened that the driver never logged. */
export interface AddSelection {
  eventType: number;
  timeUtc: string;
  latitude?: number;
  longitude?: number;
  routeLocation?: number;
}

/** One GPS wait suggestion to accept (create a real Wait pair), with the chosen reason. */
export interface WaitAccept {
  suggestedEventId: number;
  reason: string;
}

/** Re-point one existing driver event to the dispatched point-of-service GPS confirms the van visited. */
export interface RepointSelection {
  eventId: number;
  routeLocation: number;
}

/** An offered RouteLocation re-point: the mis-geofenced point vs. the dispatched one GPS confirms. */
export interface RepointSuggestion {
  eventId: number;
  eventTypeName: string;
  timeLocal: string;
  currentRouteLocation?: number;
  currentName?: string;
  newRouteLocation: number;
  newName?: string;
  nearMeters: number;
}

/** A driver wait the apply auto-removed because a re-timed anchor split it (GPS didn't back the wait then). */
export interface RemovedWaitInfo {
  eventId: number;
  startLocal: string;
  endLocal: string;
  reason?: string;
}

/** The corrections a closer has chosen to apply. Drives both preview (rolled back) and apply (committed). */
export interface AutoCloseSelection {
  removeEventIds: number[];
  retimes: RetimeSelection[];
  adds: AddSelection[];
  /** GPS wait suggestions to accept (apply only). */
  waitAccepts?: WaitAccept[];
  /** SuggestedEvent ids to dismiss (apply only). */
  waitDismissSuggestionIds?: number[];
  /** Existing driver events to re-point to the dispatched POS (GPS-confirmed). */
  repoints?: RepointSelection[];
}

/** One client wait reason and whether it's billable. */
export interface WaitReasonOption {
  name: string;
  billable: boolean;
}

export interface AutoClosePreviewResponse {
  tripId: number;
  note?: string;
  predicted: boolean;
  /** True when this was a committed apply (not a preview); applyId is then the grading-history id. */
  committed: boolean;
  applyId?: number;
  /** Set when the trip won't auto-approve regardless of GPS repair (reopened, locked, non-approving client, legacy, already approved). */
  approveBlockReason?: string;
  /** Client wait reasons + billable flag — mid-trip wait reasons are restricted to billable ones. */
  waitReasons: WaitReasonOption[];
  /** GPS-confirmed RouteLocation re-point suggestions — shown as checkbox proposed changes. */
  repointSuggestions: RepointSuggestion[];
  /** Waits the apply auto-removed because a re-timed anchor split them (surfaced as "removed due to re-time"). */
  removedWaits: RemovedWaitInfo[];
  outcome?: AutoClosePreviewOutcome;
  /** Why the trip didn't auto-approve originally (all blockers, from the production approver log). */
  originalFailReasons: string[];
  driverEvents: AutoClosePreviewEvent[];
  botEvents: AutoClosePreviewEvent[];
}

const reconstructTrip = async (tripId: number): Promise<TripReconstructionResponse> => {
  return apiClient.get(`/reconstruction/trip/${tripId}`);
};

const emptySelection = (): AutoCloseSelection => ({ removeEventIds: [], retimes: [], adds: [] });

/** Preview the outcome with the given corrections applied — runs the approver inside a rolled-back tran. */
const getAutoClosePreview = async (
  tripId: number,
  selection?: AutoCloseSelection
): Promise<AutoClosePreviewResponse> => {
  return apiClient.post(`/reconstruction/trip/${tripId}/autoclose-preview`, selection ?? emptySelection());
};

/** Commit the corrections, run the real approver, and record the apply. The one write path. */
const applyAutoClose = async (
  tripId: number,
  selection: AutoCloseSelection
): Promise<AutoClosePreviewResponse> => {
  return apiClient.post(`/reconstruction/trip/${tripId}/autoclose-apply`, selection);
};

const ReconstructionService = {
  reconstructTrip,
  getAutoClosePreview,
  applyAutoClose,
};

export default ReconstructionService;
